import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { ChatAppResponse } from "@/api/models"

import { Eye, Share2 } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { SidebarInset } from "@/components/ui/sidebar"
import { Chat } from "@/features/chat/components/chat"
import { ChatShareDialog } from "@/features/chat/components/chat-share-dialog"
import { SharedHistoryMarkerLine } from "@/features/chat/components/shared-history-marker"
import { StartNewChat } from "@/features/chat/components/start-new-chat"
import { useChatHistory } from "@/features/chat/hooks/use-chat-history"
import { useSessionsQuery } from "@/features/chat/hooks/use-sessions-query"
import { useSharedHistoryMarkers } from "@/features/chat/hooks/use-shared-history-markers"
import { getSessionErrorCode } from "@/features/chat/utils/session-error-code"
import { UploadVideo } from "@/features/video-insight/components/upload-video"
import { useGetUploadProgress } from "@/features/video-insight/hooks/use-get-upload-progress"
import { useGetUploadUrl } from "@/features/video-insight/hooks/use-get-upload-url"
import { useUploadVideo } from "@/features/video-insight/hooks/use-upload-video"
import { useVideoInsightChat } from "@/features/video-insight/hooks/use-video-insight-chat"
import { useContextMenuActions } from "@/hooks/use-context-menu-actions"
import { useCurrentSessionType } from "@/hooks/use-current-session-type"
import { useMarkSessionNotificationViewed } from "@/hooks/use-session-sharing"
import { logger } from "@/lib/logger"
import { SettingsService } from "@/services/settings-service"
import { usePendingMessageStore } from "@/stores/pending-message-store"
import { useVideoInsightStore } from "@/stores/video-insight-store"

export function VideoInsightPage() {
  const { t } = useTranslation()

  // Determine session type from current route
  const sessionType = useCurrentSessionType()

  const [isUploadCompleted, setIsUploadCompleted] = useState(false)
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(["English"])
  const [showShareDialog, setShowShareDialog] = useState(false)
  const [shareMessageId, setShareMessageId] = useState<string | undefined>()
  const handleManageSharing = useCallback((messageId?: string) => {
    setShareMessageId(messageId)
    setShowShareDialog(true)
  }, [])

  const videoStore = useVideoInsightStore(sessionType)

  // Sync URL sessionId → ChatService at page level. Below the 1024px mobile
  // breakpoint the sidebar (and its ChatHistory) is an unmounted Sheet (#3515).
  useChatHistory({ sessionType, useChatStore: () => videoStore })

  const {
    handleSendQuestion,
    handleCancelRequest,
    chatMessageStreamEnd,
    aborted,
    chatState,
    moduleName,
    handleStartNewChat,
    handleSharedLeaveRedirect,
    totalTokenCount,
    changeTokenCounts,
    handleAddFiles,
    handleResetFiles,
    currentSessionId,
  } = useVideoInsightChat()

  // Fetch sessions to check if current user is a reader
  const { data: sessions = [] } = useSessionsQuery(sessionType)

  // Check if current user is a reader (cannot send messages)
  const currentSession = useMemo(
    () => sessions.find((s) => s?.id === currentSessionId),
    [sessions, currentSessionId]
  )
  const isReader = currentSession?.currentUserRole === "reader"

  // Get current user's access mode from session participants
  const currentUserOid = SettingsService.getInstance().claims.oid
  const currentUserParticipant = useMemo(() => {
    if (!currentSession?.participants || !currentUserOid) return undefined

    // Get user's groups from settings service
    const userGroups = SettingsService.getInstance().claims.groups || []
    const userGroupsSet = new Set(userGroups)

    // Find participant: check direct user OR group membership
    return currentSession.participants.find(
      (p) =>
        (p.type === "user" && p.oid === currentUserOid) ||
        (p.type === "group" && userGroupsSet.has(p.oid))
    )
  }, [currentSession?.participants, currentUserOid])
  const currentUserAccessMode = currentUserParticipant?.messageAccessMode

  // Get current message ID (from latest message in chat)
  const answersToDisplay = useMemo(
    () =>
      chatState.streamingAnswer
        ? [...chatState.messages, chatState.streamingAnswer]
        : chatState.messages,
    [chatState.messages, chatState.streamingAnswer]
  )

  const { sharedHistoryMarkers } = useSharedHistoryMarkers(
    answersToDisplay,
    currentSession?.participants,
    currentUserParticipant
  )
  const currentMessageId = useMemo(() => {
    if (answersToDisplay.length === 0) return undefined
    const latestMessage = answersToDisplay[answersToDisplay.length - 1]
    if (!Array.isArray(latestMessage)) {
      return undefined
    }
    const response: ChatAppResponse = latestMessage[1]
    return response.messageId ?? response.context.message_id ?? undefined
  }, [answersToDisplay])

  // Mark notification as viewed when user opens a shared session
  const markNotificationViewed = useMarkSessionNotificationViewed()
  const markedSessionRef = useRef<string | null>(null)
  useEffect(() => {
    if (
      currentSessionId &&
      currentSession?.hasUnviewedNotification &&
      markedSessionRef.current !== currentSessionId
    ) {
      markedSessionRef.current = currentSessionId
      markNotificationViewed.mutate(currentSessionId)
    }
    // Reset ref when session changes (so we can mark new session if needed)
    if (
      currentSessionId &&
      markedSessionRef.current !== currentSessionId &&
      !currentSession?.hasUnviewedNotification
    ) {
      markedSessionRef.current = currentSessionId
    }
  }, [currentSessionId, currentSession?.hasUnviewedNotification])

  const { getUploadUrlMutation } = useGetUploadUrl()
  const { uploadVideoMutation } = useUploadVideo()

  const { getUploadProgressQuery } = useGetUploadProgress({
    videoId: uploadVideoMutation.data?.id ?? "",
    queryConfig: {
      refetchInterval: 5000,
      enabled: uploadVideoMutation.isSuccess && !isUploadCompleted,
    },
  })

  useEffect(() => {
    if (
      getUploadProgressQuery.data?.progress === "100%" ||
      getUploadProgressQuery.data?.state === "Processed"
    ) {
      setIsUploadCompleted(true)
    }
  }, [getUploadProgressQuery.data?.progress])

  const isUploading = getUploadUrlMutation.isPending || uploadVideoMutation.isPending

  // Handle pending message from "break out to new chat"
  const pendingMessage = usePendingMessageStore((state) => state.pendingMessage)
  const clearPendingMessage = usePendingMessageStore((state) => state.clearPendingMessage)

  // Context menu actions for break out functionality
  const { handleBreakOutChat: contextMenuBreakOut } = useContextMenuActions({
    currentSessionId,
    onSendQuestion: handleSendQuestion,
    sessionType: sessionType,
  })

  // Handler for breaking out from AI response button
  const handleBreakOutFromAIResponse = useCallback(
    async (content: string) => {
      try {
        logger.info("Break out button clicked", { contentLength: content.length })
        await contextMenuBreakOut(content, "user")
      } catch (error) {
        logger.error("Break out button failed", error as Error)
        toast.error("Failed to create new chat")
      }
    },
    [contextMenuBreakOut]
  )

  // Callback to handle sharing a specific message
  const handleShareMessage = useCallback((messageId?: string) => {
    setShareMessageId(messageId)
    setShowShareDialog(true)
  }, [])

  useEffect(() => {
    // Check if we have a pending message AND we're in a different session
    if (
      pendingMessage &&
      (currentSessionId !== pendingMessage.sourceSessionId || currentSessionId === null)
    ) {
      // Small delay to ensure the chat is fully rendered
      const timer = setTimeout(() => {
        logger.info("Processing pending assistant message", {
          textLength: pendingMessage.text.length,
          sourceSessionId: pendingMessage.sourceSessionId,
        })
        const statusLine = pendingMessage.sourceSessionId
          ? `🔗 Broken out from [${pendingMessage.sourceSessionTitle || "previous chat"}](/video-insight/chat/${pendingMessage.sourceSessionId})\n\n`
          : `🔗 Broken out from previous chat\n\n`
        const displayContent = statusLine + pendingMessage.text

        // Inject message into chat state
        const changeChatState = videoStore.getState().changeChatState
        // Read chatState inside changeChatState to avoid overwriting concurrent updates
        const currentSessionId = videoStore.getState().currentSessionId
        const state = videoStore.getState()
        const currentMessages =
          currentSessionId && currentSessionId in state.sessionStates
            ? state.sessionStates[currentSessionId].messages
            : []
        changeChatState({
          messages: [
            ...currentMessages,
            [
              "", // Empty user message
              {
                content: displayContent,
                context: {
                  data_points: [],
                  followup_questions: null,
                  thoughts: [],
                },
                session_state: null,
                completed: true,
              },
            ],
          ],
        })

        toast.info("Chat created", {
          description: `Broken out from "${pendingMessage.sourceSessionTitle || "previous chat"}"`,
        })

        clearPendingMessage()
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [pendingMessage, currentSessionId, clearPendingMessage])

  return (
    <Chat>
      <SidebarInset className="module-enter relative h-[calc(100svh-64px)] min-h-[calc(100svh-64px)] overflow-hidden md:peer-data-[variant=inset]:mr-0">
        <Chat.Header className="absolute top-0 right-0 left-0 z-50 justify-between lg:justify-end">
          <div className="mr-auto flex items-center gap-2">
            <div className="lg:hidden">
              <StartNewChat
                onStartNewChatClick={() => {
                  setIsUploadCompleted(false)
                  handleStartNewChat()
                }}
              />
            </div>
            {isReader && (
              <Badge
                className="border-foreground bg-accent text-accent-foreground h-7 border px-3"
                variant="outline"
              >
                <Eye className="mr-1.5 size-3" />
                {t("chatsession.reader_badge", { defaultValue: "READER" })}
              </Badge>
            )}
            {currentSessionId && (
              <Button
                className="h-7 gap-1.5 border-none px-3 shadow-none"
                size="sm"
                variant="ghost"
                onClick={() => setShowShareDialog(true)}
              >
                <Share2 className="size-3" />
                {t("sharing.share", { defaultValue: "Share" })}
              </Button>
            )}
          </div>
        </Chat.Header>
        <Chat.Content>
          {(() => {
            return (
              <>
                {answersToDisplay.length === 0 &&
                  !chatState.lastQuestion[0] &&
                  !chatState.isLoading &&
                  !chatState.isHydrating &&
                  !chatState.streamingAnswer && (
                    <UploadVideo
                      isLoading={isUploading}
                      selectedLanguages={selectedLanguages}
                      setSelectedLanguages={setSelectedLanguages}
                      onFileAdded={(file) => {
                        handleResetFiles()
                        handleAddFiles([file])
                      }}
                      onUpload={async (file) => {
                        const { upload_url: uploadUrl } = await getUploadUrlMutation.mutateAsync({
                          videoName: file.name,
                        })

                        const videoLanguage = videoStore.getState().videoLanguage

                        const { id: videoId } = await uploadVideoMutation.mutateAsync({
                          url: `${uploadUrl}&language=${videoLanguage}`,
                          file,
                        })

                        await handleSendQuestion({
                          question: file.name,
                          videoId,
                          selectedLanguages,
                        })
                      }}
                    />
                  )}
                {sharedHistoryMarkers.top.length > 0 && (
                  <SharedHistoryMarkerLine
                    markers={sharedHistoryMarkers.top}
                    onManageSharing={handleManageSharing}
                  />
                )}
                {answersToDisplay.map((answer: [string, ChatAppResponse], index: number) => {
                  const shouldShowUserMessage =
                    currentUserAccessMode !== "specific_message" && answer[0] && answer[0].trim()

                  const answerKey = answer[1].context.message_id || answer[1].messageId || index
                  const messageId = answer[1].messageId || answer[1].context.message_id
                  const beforeMarkers = messageId
                    ? sharedHistoryMarkers.before[messageId]
                    : undefined
                  const afterMarkers = messageId ? sharedHistoryMarkers.after[messageId] : undefined
                  return (
                    <section key={answerKey} className="flex flex-col gap-6">
                      {beforeMarkers?.length ? (
                        <SharedHistoryMarkerLine
                          markers={beforeMarkers}
                          onManageSharing={handleManageSharing}
                        />
                      ) : null}
                      {shouldShowUserMessage && <Chat.UserMessage message={answer[0]} />}
                      <Chat.SystemMessage
                        answer={answer[1]}
                        isLastMessage={index === answersToDisplay.length - 1}
                        isReader={isReader}
                        messageId={answer[1].messageId}
                        moduleName={moduleName}
                        noCitation={false}
                        sessionId={currentSessionId ?? undefined}
                        sessionType={sessionType}
                        showAnalysisPanelIcon={true}
                        showCopyRaw={true}
                        showDownload={true}
                        showFollowupQuestions={false}
                        isStreaming={
                          Boolean(chatState.streamingAnswer) &&
                          index === answersToDisplay.length - 1
                        }
                        onBreakOutChat={handleBreakOutFromAIResponse}
                        onShareMessage={handleShareMessage}
                      />
                      {afterMarkers?.length ? (
                        <SharedHistoryMarkerLine
                          markers={afterMarkers}
                          onManageSharing={handleManageSharing}
                        />
                      ) : null}
                    </section>
                  )
                })}
                {sharedHistoryMarkers.bottom.length > 0 && (
                  <SharedHistoryMarkerLine
                    markers={sharedHistoryMarkers.bottom}
                    onManageSharing={handleManageSharing}
                  />
                )}
              </>
            )
          })()}
          {chatState.isLoading && !chatState.streamingAnswer && (
            <>
              {currentUserAccessMode !== "specific_message" && chatState.lastQuestion[0] && (
                <Chat.UserMessage message={chatState.lastQuestion[0]} />
              )}
              <Chat.Loading
                citationSection={currentSessionId ?? undefined}
                message={
                  getUploadProgressQuery.data?.progress
                    ? `${t("pages.video_insight.uploading")} ${getUploadProgressQuery.data.progress}...`
                    : `${t("pages.video_insight.uploading")}...`
                }
              />
            </>
          )}
          {chatState.error ? (
            <>
              {currentUserAccessMode !== "specific_message" && chatState.lastQuestion[0] && (
                <Chat.UserMessage message={chatState.lastQuestion[0]} />
              )}
              <Chat.Error
                error={(chatState.error as Error).toString()}
                errorCode={getSessionErrorCode(chatState)}
                onRetry={() => handleSendQuestion({ question: chatState.lastQuestion[0] })}
              />
            </>
          ) : null}
          {aborted ? (
            <>
              {currentUserAccessMode !== "specific_message" && aborted[0] && (
                <Chat.UserMessage message={aborted[0]} />
              )}
              <Chat.Error
                error={t("pages.common.request_aborted") + "\n\n" + (aborted[1] ? aborted[1] : "")}
                onRetry={() => handleSendQuestion({ question: aborted[0] })}
              />
            </>
          ) : null}
          <div ref={chatMessageStreamEnd} />
        </Chat.Content>
        {chatState.lastQuestion[0] && (
          <Chat.Footer
            clearOnSend
            changeTokenCounts={changeTokenCounts}
            disableFileSelection={true}
            isReader={isReader}
            maxTokens={Number.POSITIVE_INFINITY}
            placeholder={t("questioninput.video_insight.placeholder")}
            showCancelOption={chatState.isLoading || chatState.isStreaming}
            totalTokenCount={totalTokenCount}
            disableSend={
              isReader || chatState.isLoading || chatState.isStreaming || chatState.isAwaitingSave
            }
            onCancel={handleCancelRequest}
            onSend={async (question) => {
              await handleSendQuestion({
                question,
                selectedLanguages,
              })
            }}
          />
        )}
      </SidebarInset>

      {currentSessionId && (
        <ChatShareDialog
          chatStore={videoStore}
          currentMessageId={shareMessageId ?? currentMessageId}
          open={showShareDialog}
          session={currentSession}
          sessionId={currentSessionId}
          onLeave={handleSharedLeaveRedirect}
          onOpenChange={(open) => {
            setShowShareDialog(open)
            // Reset shareMessageId when dialog closes
            if (!open) {
              setShareMessageId(undefined)
            }
          }}
        />
      )}
    </Chat>
  )
}
